<template>
  <div class="phone-form-container">
    <!-- Logo和描述文字容器 -->
    <div class="main-logo-container">
      <h1 class="login-title">{{ logoText }}</h1>
      <p v-if="showDescription" class="feature-description">
        <span>标签管理</span>
        <span class="separator">|</span>
        <span>全平台作品下载</span>
        <span class="separator">|</span>
        <span>多端适配</span>
      </p>
    </div>
    
    <form @submit.prevent="handleSubmit" class="register-form">
      <div class="input-group">
        <input 
          class="register-input" 
          id="username" 
          name="username" 
          placeholder="账号" 
          type="text"
          v-model="formData.username"
          maxlength="20"
          autocomplete="username"
          @focus="handleInputFocus"
          style="-webkit-appearance: none; appearance: none; background-color: transparent; background-image: none; box-shadow: none; -webkit-box-shadow: none;"
        />
      </div>
      <div class="input-group">
        <input 
          class="register-input" 
          id="password" 
          name="password" 
          placeholder="密码" 
          type="password"
          v-model="formData.password"
          maxlength="20"
          autocomplete="new-password"
          @focus="handleInputFocus"
          style="-webkit-appearance: none; appearance: none; background-color: transparent; background-image: none; box-shadow: none; -webkit-box-shadow: none;"
        />
      </div>
      <div class="input-group">
        <input 
          class="register-input" 
          id="confirm-password" 
          name="confirm-password" 
          placeholder="确认密码" 
          type="password"
          v-model="formData.confirmPassword"
          maxlength="20"
          autocomplete="new-password"
          @focus="handleInputFocus"
          style="-webkit-appearance: none; appearance: none; background-color: transparent; background-image: none; box-shadow: none; -webkit-box-shadow: none;"
        />
      </div>
      <div class="input-group" v-if="requireInviteCode">
        <input 
          class="register-input" 
          id="invite-code" 
          name="invite-code" 
          placeholder="邀请码" 
          type="text"
          v-model="formData.inviteCode"
          maxlength="64"
          autocomplete="off"
          @focus="handleInputFocus"
          style="-webkit-appearance: none; appearance: none; background-color: transparent; background-image: none; box-shadow: none; -webkit-box-shadow: none;"
        />
      </div>
    </form>
    
    <div class="register-action-container">
      <button 
        class="register-button" 
        :disabled="isLoading"
        @click="handleSubmit"
      >
        {{ isLoading ? '注册中...' : '注册' }}
      </button>
      <div class="login-link">
        <p>已经有账户了？ 
          <a 
            href="#" 
            @click.prevent="$emit('switch-to-login')"
          >
            登录
          </a>
        </p>
      </div>
    </div>
    
    <!-- Logo 区域（可配置显示） -->
    <div v-if="showLogoSection" class="logo-section">
      <div class="logo-container">
        <!-- 微软 Logo -->
        <div class="microsoft-logo">
          <img src="@/assets/images/background/Microsoft-logo.png" alt="Microsoft" class="logo-image" />
        </div>
        
        <!-- 分隔线 -->
        <div class="logo-divider"></div>
        
        <!-- imusic Logo -->
        <div class="imusic-logo">
          <span class="imusic-text">imusic</span>
        </div>
      </div>
      <!-- 存储支持说明 -->
      <div class="storage-note">
        <span class="storage-text">Microsoft OneDrive提供存储支持</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { REGISTER_REQUIRE_INVITE_CODE } from '@/constants/registerConfig'

const requireInviteCode = REGISTER_REQUIRE_INVITE_CODE
// Props
const props = defineProps({
  isLoading: {
    type: Boolean,
    default: false
  },
  errorMessage: {
    type: String,
    default: ''
  },
  registerSuccess: {
    type: Boolean,
    default: false
  },
  logoText: {
    type: String,
    default: 'imusic'
  },
  showDescription: {
    type: Boolean,
    default: true
  },
  showLogoSection: {
    type: Boolean,
    default: true
  },
  scrollIntoViewOnFocus: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['submit', 'switch-to-login'])

// 表单数据
const formData = reactive({
  username: '',
  password: '',
  confirmPassword: '',
  inviteCode: ''
})

// 处理输入框聚焦
const handleInputFocus = (event) => {
  if (!event?.target) return
  requestAnimationFrame(() => {
    event.target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

// 处理提交
const handleSubmit = () => {
  emit('submit', {
    ...formData
  })
}

// 暴露方法供父组件调用
defineExpose({
  formData,
  resetForm: () => {
    formData.username = ''
    formData.password = ''
    formData.confirmPassword = ''
    formData.inviteCode = ''
  }
})
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Onest:wght@100..900&family=Rubik:ital,wght@0,300..900;1,300..900&family=Pattaya:wght@400&display=swap');
</style>

<style scoped>
/* 表单容器 */
.phone-form-container {
  background: rgba(255, 255, 255, 0.541);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-radius: 35px !important;
  padding: 0;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border: none;
  margin-left: 4px;
  width: 100%;
  max-width: 400px;
  position: relative;
  height: 100%;
  z-index: 2;
}

/* PC端和平板端：保持尺寸，去掉手机壳样式 */
@media (min-width: 768px) {
  .phone-form-container {
    height: 100%;
    width: 100%;
    aspect-ratio: 386 / 837;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    margin-left: 0;
  }
  
  .phone-form-container .main-logo-container {
    top: 25% !important;
  }
  
  .register-form {
    top: 46% !important;
    width: 50% !important;
  }
  
  .register-action-container {
    top: 73% !important;
    width: 80% !important;
  }
  
  .logo-section {
    width: 90% !important;
  }

  .logo-container {
    width: 67% !important;
  }

  .imusic-text {
    font-size: 1rem !important;
    transform: translateY(-0rem);
  }

  .logo-image {
    height: 1.1rem !important;
  }
}

/* 主Logo容器 */
.phone-form-container .main-logo-container {
  text-align: center;
  margin-bottom: 0;
  display: block !important;
  position: absolute;
  top: calc(var(--auth-locked-vh, 1vh) * 20) !important;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

/* 登录标题 */
.login-title {
  margin-left: -5px;
  font-size: 50px !important;
  font-weight: 700;
  text-align: center;
  margin-bottom: -11px !important;
  color: #000000;
  letter-spacing: -0.02em;
  position: relative;
  top: -50px;
}

/* 特色描述文本 */
.phone-form-container .feature-description {
  text-align: center;
  color: rgba(0, 0, 0, 0.7);
  font-size: 11px;
  font-weight: 700;
  margin-top: 0px;
  margin-bottom: 50px;
  letter-spacing: 0.5px;
  text-shadow: none;
  font-family: 'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: block !important;
  position: static;
  top: auto;
  transform: translateY(-15px);
}

/* 分隔符样式 */
.feature-description .separator {
  color: rgba(0, 0, 0, 0.4);
  margin: 0 8px;
  font-weight: 300;
  font-size: 12px;
}

/* 注册表单 */
.register-form {
  position: absolute;
  top: calc(var(--auth-locked-vh, 1vh) * 46);
  left: 50%;
  transform: translate(-50%, -50%);

  margin: 0;
  box-sizing: border-box;
  overflow: hidden;
}

/* 输入框组 */
.input-group {
  margin-bottom: 0rem !important;
}

.input-group:last-child {
  margin-bottom: 2rem;
}

/* 注册输入框 */
.register-input {
  width: 71%;
  min-width: 215px !important;
  margin: 0 auto;
  display: block;
  padding: 0.6rem 0.3rem 0.2rem 0.3rem;
  background: transparent;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  color: #000000;
  font-size: 1.05rem;
  font-weight: 700;
  font-family: 'Onest', 'Rubik', 'Zalando Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  letter-spacing: 0px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  outline: none !important;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow: none !important;
  transform: translateX(-0rem);
  /* 🍎 iOS reset：去除原生UIKit渲染 */
  -webkit-appearance: none;
  appearance: none;
  -webkit-text-fill-color: #000000;
  -webkit-tap-highlight-color: transparent;
}

.register-input::placeholder {
  color: rgba(0, 0, 0, 0.5);
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: normal;
  transform: translateX(-5px);
}

.register-input[type="password"] {
  letter-spacing: 2px;
}



.register-input:focus::placeholder {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.register-input:-webkit-autofill,
.register-input:-webkit-autofill:hover,
.register-input:-webkit-autofill:focus,
.register-input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  -webkit-text-fill-color: #000000 !important;
  background: transparent !important;
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2) !important;
  transition: background-color 5000s ease-in-out 0s;
}

.register-input:-moz-autofill {
  background: transparent !important;
  color: #000000 !important;
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.2) !important;
}

/* 注册操作容器 */
.register-action-container {
  position: absolute;
  top: calc(var(--auth-locked-vh, 1vh) * 65);
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  box-sizing: border-box;
}

/* 注册按钮 */
.register-button {
  width: 80% !important;
  min-width: 100px !important;
  margin: 0 auto;
  display: block;
  padding: 1rem 1.5rem;
  background: #007aff !important;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  position: relative;
  overflow: hidden;
  opacity: 1;
  box-shadow: 0 6px 16px rgba(0, 122, 255, 0.35);
}

.register-button:hover {
  background: #005ed2 !important;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 122, 255, 0.4);
}

.register-button:active {
  transform: translateY(0);
  background: #006ae6 !important;
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.35);
}

.register-button:disabled {
  background: rgba(0, 122, 255, 0.3) !important;
  color: rgba(255, 255, 255, 0.9) !important;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 登录链接 */
.login-link {
  text-align: center;
  transform: translateY(20px);
  margin-bottom: 25px;
}

.login-link p {
  color: rgba(0, 0, 0, 0.7);
  font-size: 0.875rem;
}

.login-link a {
  color: rgba(0, 0, 0, 0.8);
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.login-link a:hover {
  text-decoration: underline;
  color: #000000;
}

/* Logo 区域样式 */
.logo-section {
  position: absolute;
  bottom: calc(var(--auth-locked-vh, 1vh) * 5.5);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 75%;
  padding: 0.2rem 1rem;
  background: transparent;
  border-radius: 5rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  margin: 0;
}

/* 微软 Logo */
.microsoft-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  transform: scale(0.82);
}

.logo-image {
  height: 24px;
  width: auto;
  opacity: 0.8;
  transition: opacity 0.3s ease;
}

.logo-image:hover {
  opacity: 1;
}

/* 分隔线 */
.logo-divider {
  width: 1px;
  height: 20px;
  background: rgba(0, 0, 0, 0.2);
  margin: 0 0.5rem;
  flex-shrink: 0;
}

/* imusic Logo */
.imusic-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.imusic-text {
  font-family: 'Oleo_Script', 'Onest', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  letter-spacing: -0.025em;
  text-shadow: none;
  transition: color 0.3s ease;
}

.imusic-text:hover {
  color: rgba(0, 0, 0, 0.9);
}

/* 存储支持说明 */
.storage-note {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
}

.storage-text {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.5);
  font-weight: 400;
  letter-spacing: 0.025em;
  text-align: center;
  line-height: 1.4;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .phone-form-container {
    font-size: 11.7px;
    max-height: 600px !important;
  }

  .login-title {
    top: -80px !important;
  }
  
  .feature-description .separator {
    font-size: 10px;
    margin: 0 6px;
  }

  .register-form {
    top: 290px !important;

  }

  .register-action-container {
    top: 480px !important;
    width: 87% !important;
  }

  .logo-container {
    width: 72.5% !important;
  }

  .imusic-text {
    font-size: 1rem !important;
    transform: translateY(-0.1rem);
  }
  
  .logo-image {
    height: 1.1rem !important;
  }
}

@media (max-width: 480px) {
  .logo-container {
    padding: 0.5rem 1rem;
    gap: 0.75rem;
  }
  
  .logo-image {
    height: 20px;
  }
  
  .imusic-text {
    font-size: 1.25rem;
  }
  
  .logo-divider {
    height: 16px;
    margin: 0 0.25rem;
  }
  
  .storage-text {
    font-size: 0.7rem;
  }
}

@media (max-width: 360px) {
  .logo-container {
    padding: 0.3rem 0.8rem;
    gap: 0.5rem;
  }
  
  .logo-image {
    height: 18px;
  }
  
  .imusic-text {
    font-size: 1.125rem;
  }
  
  .storage-text {
    font-size: 0.65rem;
  }
}
</style>
